<template>
  <div class="px-4 py-4 mb-14">
    <div class="mb-4">
      <h2 class="text-sm font-semibold text-neutral-800 mb-1">Deine Kontaktdaten</h2>
    </div>
    
    <div class="space-y-3 mb-6">
      <div>
        <label class="block text-xs font-medium text-neutral-600 mb-1">
          Name <span class="text-red-500">*</span>
        </label>
        <input 
          type="text" 
          v-model="localFormData.name"
          placeholder="Vollständiger Name"
          class="form-input"
          :style="{
            width: '100%',
            backgroundColor: nameError ? '#fef2f2' : '#f9fafb',
            border: nameError ? '1px solid #ef4444' : '1px solid #e5e7eb',
            padding: '0.5rem 0.75rem',
            borderRadius: '0.375rem',
            fontSize: '0.75rem',
            color: '#374151',
            transition: 'all 0.2s ease'
          }"
          @focus="handleInputFocus($event, !!nameError)"
          @blur="handleInputBlur($event, !!nameError)"
          @mouseover="handleInputHover($event, !!nameError)"
          @mouseout="handleInputMouseOut($event, !!nameError)"
        />
        <p v-if="nameError" class="text-red-500 text-xs mt-1">{{ nameError }}</p>
      </div>
      
      <div>
        <label class="block text-xs font-medium text-neutral-600 mb-1">
          Email <span class="text-red-500">*</span>
        </label>
        <input 
          type="email" 
          v-model="localFormData.email"
          placeholder=""
          class="form-input"
          :style="{
            width: '100%',
            backgroundColor: emailError ? '#fef2f2' : '#f9fafb',
            border: emailError ? '1px solid #ef4444' : '1px solid #e5e7eb',
            padding: '0.5rem 0.75rem',
            borderRadius: '0.375rem',
            fontSize: '0.75rem',
            color: '#374151',
            transition: 'all 0.2s ease'
          }"
          @focus="handleInputFocus($event, !!emailError)"
          @blur="handleInputBlur($event, !!emailError)"
          @mouseover="handleInputHover($event, !!emailError)"
          @mouseout="handleInputMouseOut($event, !!emailError)"
        />
        <p v-if="emailError" class="text-red-500 text-xs mt-1">{{ emailError }}</p>
      </div>
      
      <div>
        <label class="block text-xs font-medium text-neutral-600 mb-1">Telefon</label>
        <input 
          type="tel" 
          v-model="localFormData.phone"
          placeholder="+41..."
          class="form-input"
          :style="{
            width: '100%',
            backgroundColor: '#f9fafb',
            border: '1px solid #e5e7eb',
            padding: '0.5rem 0.75rem',
            borderRadius: '0.375rem',
            fontSize: '0.75rem',
            color: '#374151',
            transition: 'all 0.2s ease'
          }"
          @focus="handleInputFocus($event, false)"
          @blur="handleInputBlur($event, false)"
          @mouseover="handleInputHover($event, false)"
          @mouseout="handleInputMouseOut($event, false)"
        />
      </div>
      
      <div>
        <label class="block text-xs font-medium text-neutral-600 mb-1">Unternehmen</label>
        <input 
          type="text" 
          v-model="localFormData.company"
          placeholder="Name deines Unternehmens"
          class="form-input"
          :style="{
            width: '100%',
            backgroundColor: '#f9fafb',
            border: '1px solid #e5e7eb',
            padding: '0.5rem 0.75rem',
            borderRadius: '0.375rem',
            fontSize: '0.75rem',
            color: '#374151',
            transition: 'all 0.2s ease'
          }"
          @focus="handleInputFocus($event, false)"
          @blur="handleInputBlur($event, false)"
          @mouseover="handleInputHover($event, false)"
          @mouseout="handleInputMouseOut($event, false)"
        />
      </div>
    </div>

    <!-- Navigation -->
    <div class="flex justify-between">
      <button 
        @click="prevStep"
        class="back-button"
        :style="{
          padding: '0.6rem 1rem',
          fontSize: '0.75rem',
          fontWeight: '500',
          borderRadius: '0.375rem',
          border: '1px solid #e5e7eb',
          backgroundColor: '#f9fafb',
          color: '#374151',
          cursor: 'pointer',
          transition: 'all 0.2s ease'
        }"
        @mouseover="handleBackButtonHover"
        @mouseout="handleBackButtonMouseOut"
      >
        Zurück
      </button>
      
      <button 
        @click="nextStep"
        class="continue-button"
        :style="{
          padding: '0.6rem 1rem',
          fontSize: '0.75rem',
          fontWeight: '500',
          borderRadius: '0.375rem',
          border: 'none',
          cursor: isValid ? 'pointer' : 'not-allowed',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          opacity: isValid ? '1' : '0.7',
          ...getContinueButtonStyle()
        }"
        :disabled="!isValid"
        @mouseover="handleContinueButtonHover"
        @mouseout="handleContinueButtonMouseOut"
      >
        Weiter
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'MeetingStep2',
  props: {
    formData: {
      type: Object,
      required: true
    },
    styles: {
      type: Object,
      default: () => ({
        bubbleColor: '#3b82f6',
        textColor: '#ffffff'
      })
    }
  },
  data() {
    return {
      localFormData: { ...this.formData }
    };
  },
  computed: {
    nameError() {
      if (this.localFormData.name && this.localFormData.name.trim() === '') {
        return 'Name erforderlich';
      }
      if (this.localFormData.name && this.localFormData.name.trim().length < 2) {
        return 'Name muss mindestens 2 Buchstaben haben';
      }
      return null;
    },
    
    emailError() {
      if (this.localFormData.email && !this.isValidEmail(this.localFormData.email)) {
        return 'Bitte gib eine gültige E-Mail Adresse ein';
      }
      return null;
    },
    
    isValid() {
      const hasName = this.localFormData.name && this.localFormData.name.trim().length >= 2;
      const hasValidEmail = this.localFormData.email && this.isValidEmail(this.localFormData.email);
      
      console.log('MeetingStep2 validation:', {
        name: this.localFormData.name,
        email: this.localFormData.email,
        hasName,
        hasValidEmail,
        isValid: hasName && hasValidEmail
      });
      
      return hasName && hasValidEmail;
    }
  },
  watch: {
    localFormData: {
      deep: true,
      handler(newValue) {
        console.log('MeetingStep2 form data updated:', newValue);
        this.$emit('update:form-data', newValue);
      }
    }
  },
  mounted() {
    console.log('MeetingStep2 mounted with formData:', this.formData);
    console.log('MeetingStep2 mounted with styles:', this.styles);
    console.log('Initial localFormData:', this.localFormData);
  },
  methods: {
    // Handle input focus/hover for Shadow DOM
    handleInputFocus(event, hasError) {
      if (hasError) {
        event.target.style.borderColor = '#ef4444';
        event.target.style.boxShadow = '0 0 0 1px #ef4444';
      } else {
        event.target.style.borderColor = '#d1d5db';
        event.target.style.backgroundColor = 'white';
        event.target.style.boxShadow = '0 0 0 1px #d1d5db';
      }
    },
    
    handleInputBlur(event, hasError) {
      if (hasError) {
        event.target.style.borderColor = '#ef4444';
        event.target.style.backgroundColor = '#fef2f2';
        event.target.style.boxShadow = 'none';
      } else {
        event.target.style.borderColor = '#e5e7eb';
        event.target.style.backgroundColor = '#f9fafb';
        event.target.style.boxShadow = 'none';
      }
    },
    
    handleInputHover(event, hasError) {
      if (document.activeElement !== event.target && !hasError) {
        event.target.style.borderColor = '#d1d5db';
        event.target.style.backgroundColor = 'white';
      }
    },
    
    handleInputMouseOut(event, hasError) {
      if (document.activeElement !== event.target) {
        if (hasError) {
          event.target.style.borderColor = '#ef4444';
          event.target.style.backgroundColor = '#fef2f2';
        } else {
          event.target.style.borderColor = '#e5e7eb';
          event.target.style.backgroundColor = '#f9fafb';
        }
      }
    },

    // Handle back button hover for Shadow DOM
    handleBackButtonHover(event) {
      event.target.style.backgroundColor = 'white';
      event.target.style.borderColor = '#d1d5db';
    },
    
    handleBackButtonMouseOut(event) {
      event.target.style.backgroundColor = '#f9fafb';
      event.target.style.borderColor = '#e5e7eb';
    },

    // Handle continue button hover for Shadow DOM
    handleContinueButtonHover(event) {
      if (this.isValid) {
        event.target.style.transform = 'translateY(-1px)';
        event.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
      }
    },
    
    handleContinueButtonMouseOut(event) {
      if (this.isValid) {
        event.target.style.transform = 'none';
        event.target.style.boxShadow = 'none';
      }
    },

    isValidEmail(email) {
      const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return re.test(String(email).toLowerCase());
    },
    
    nextStep() {
      console.log('Next step clicked, isValid:', this.isValid);
      if (this.isValid) {
        console.log('Emitting next-step with data:', this.localFormData);
        this.$emit('next-step');
      }
    },
    
    prevStep() {
      console.log('Previous step clicked');
      this.$emit('prev-step');
    },
    
    // Get dynamic styling for continue button
    getContinueButtonStyle() {
      const bubbleColor = this.styles.bubbleColor || '#3b82f6';
      const textColor = this.styles.textColor || '#ffffff';
      
      return {
        backgroundColor: bubbleColor,
        color: textColor
      };
    }
  }
};
</script>

<style scoped>
/* Minimal styles - placeholder styling that can't be done inline */
.form-input::placeholder {
  color: #9ca3af;
  font-size: 0.75rem;
}
</style>